<script lang="ts" setup>
const { t } = useI18n()

const details = computed(() => {
  return [
    {
      icon: getIcon('supportChat'),
      label: {
        base: t('liveChat.base'),
        sm: t('liveChat.sm'),
      },
    },
    {
      icon: getIcon('calendar'),
      label: {
        base: t('openings.base'),
        sm: t('openings.sm'),
      },
    },
  ]
})

const quotes = computed(() => {
  const quotes: {
    character: BaseCharacterCharacter
    text: string
  }[] = [
    {
      character: 'nada',
      text: t('quotes.text1'),
    },
    {
      character: 'mathieu',
      text: t('quotes.text2'),
    },
    {
      character: 'nada',
      text: t('quotes.text3'),
    },
  ]

  return quotes
})
</script>

<template>
  <HomeHero
    :description="t('description')"
    orientation="horizontal"
    :title="t('title')"
  >
    <template #secondaryDescription>
      <div class="mt-8 flex flex-wrap items-center gap-3 text-base md:text-xl">
        <UButton
          v-for="(detail, detailIndex) in details"
          :key="detailIndex"
          class="flex items-center rounded-full"
          color="gray"
          :icon="detail.icon"
          size="lg"
          :ui="{ icon: { base: detailIndex === 0 ? 'text-green-400 dark:text-green-600' : 'text-indigo-400 dark:text-indigo-600' } }"
          @click="toggleChatbox('open')"
        >
          <span class="hidden md:block">{{ detail.label.base }}</span>

          <span class="md:hidden">{{ detail.label.sm }}</span>
        </UButton>
      </div>
    </template>

    <template #default>
      <div class="mx-auto flex max-w-xl flex-col justify-center gap-8">
        <BaseQuote
          v-for="(quote, quoteIndex) in quotes"
          :key="quoteIndex"
          :character="quote.character"
          :reverse="quoteIndex % 2 === 1"
          :text="quote.text"
        />
      </div>
    </template>
  </HomeHero>
</template>

<i18n lang="json">
{
  "en": {
    "description": "Missing a specific company data point? We'll add it for you in just a few days.",
    "liveChat": {
      "base": "Live chat for all customers",
      "sm": "Live chat for all customers"
    },
    "openings": {
      "base": "Monday to Friday, during business hours",
      "sm": "Monday to Friday"
    },
    "quotes": {
      "text1": "Do you have any questions? We're here to help.",
      "text2": "Hi, I need a specific datapoint for my product but it seems that you don't have in your database. Can you help me?",
      "text3": "I have notified the team about this. Let us 15 days and it will be live on the platform. Thank you for the idea!"
    },
    "title": "Real humans to help you live"
  },
  "es": {
    "description": "¿Te falta algún punto de datos de empresa específico? Lo agregaremos para usted en unos días.",
    "liveChat": {
      "base": "Chat en directo para todos los clientes",
      "sm": "Chat en directo"
    },
    "openings": {
      "base": "De lunes a viernes, durante las horas de oficina",
      "sm": "De lunes a viernes"
    },
    "quotes": {
      "text1": "¿Tienes alguna pregunta? Estamos aquí para ayudarte.",
      "text2": "Hola, necesito un punto de datos específico para mi producto, pero parece que no lo tienes en tu base de datos. ¿Puedes ayudarme?",
      "text3": "Hemos notificado al equipo sobre esto. Déjanos 15 días y estará en la plataforma. ¡Gracias por la idea!"
    },
    "title": "Humanos reales para ayudarte en directo"
  },
  "fr": {
    "description": "Il vous manque un point de données d'entreprise spécifique ? Nous l'ajouterons pour vous en quelques jours.",
    "liveChat": {
      "base": "Chat en direct pour tous les clients",
      "sm": "Chat en direct"
    },
    "openings": {
      "base": "Du lundi au vendredi, pendant les heures de bureau",
      "sm": "Du lundi au vendredi"
    },
    "quotes": {
      "text1": "Avez-vous des questions? Nous sommes là pour vous aider.",
      "text2": "Bonjour, j'ai besoin d'un datapoint spécifique pour mon produit mais il semble que vous ne l'ayez pas dans votre base de données. Pouvez-vous m'aider?",
      "text3": "J'ai informé l'équipe à ce sujet. Laissez-nous 15 jours et il sera en ligne sur la plateforme. Merci pour l'idée!"
    },
    "title": "De vrais humains pour vous aider en direct"
  }
}
</i18n>
